import dayjs from "dayjs";
import { LogEntry } from "./types";

export function formatTimestamp(iso?: string | null): string {
  if (!iso) return "";
  return dayjs(iso).format("YYYY-MM-DD HH:mm:ss");
}

const ENV_LABELS: Record<string, string> = {
  production: "Prod",
  uat: "UAT",
  pat: "PAT",
  dev: "Dev",
};

export function formatEnv(env?: string | null): string {
  if (!env) return "";
  return ENV_LABELS[env] || env;
}

const SERVICE_LABELS: Record<string, string> = {
  hotel: "Hotel",
  flight: "Flight",
  activity: "Activity",
  common: "Flight+Hotel",
  general: "General",
};

export function formatService(svc?: string | null): string {
  if (!svc) return "";
  return SERVICE_LABELS[svc] || svc;
}

export function truncate(str?: string | null, len = 80): string {
  if (!str) return "";
  return str.length > len ? str.slice(0, len) + "..." : str;
}

// Reduce a composite log line to its human-readable core so similar errors group
// together (and the table isn't dominated by request ids / URLs / raw JSON bodies).
// Priority:
//   1. An embedded JSON "message" / "displayMessage" (e.g. inside `body={...}`).
//   2. The concise `err=...` segment, after stripping a leading `[...]` request tag.
//   3. The message with just the leading `[...]` request tag removed.
// Falls back to the original string when none of these apply.
export function cleanMessage(raw?: string | null): string {
  if (!raw) return "";
  const msg = raw.trim();

  // 1) Prefer an embedded human-readable message from a JSON body.
  const embedded = msg.match(
    /"(?:displayMessage|message)"\s*:\s*"((?:\\.|[^"\\])*)"/
  );
  if (embedded && embedded[1]) {
    try {
      return JSON.parse(`"${embedded[1]}"`);
    } catch {
      return embedded[1];
    }
  }

  // 2/3) Strip a leading volatile request tag like "[v2-BE-response 473ea3f3 idx=0] ".
  const stripped = msg.replace(/^\[[^\]]*\]\s*/, "");

  // Prefer the concise `err=...` text (up to the raw `body=` dump) when present.
  const errSeg = stripped.match(/\berr=(.+?)(?:\s+body=|\s*$)/);
  if (errSeg && errSeg[1]) return errSeg[1].trim();

  return stripped;
}

// A stable, human-readable LABEL for a message — used for the Exclude-box
// suggestions. On top of cleanMessage it drops any trailing dynamic payload
// (a JSON body / object, a `key=value` dump) so entries that differ only by their
// variable tail — e.g. "Request body in searchConfig: {…}" — collapse to one
// suggestion ("Request body in searchConfig") instead of repeating.
export function messageLabel(raw?: string | null): string {
  let m = cleanMessage(raw);
  // Cut at the first JSON payload start ({ or [) if there's real text before it.
  const brace = m.search(/[{[]/);
  if (brace > 0) m = m.slice(0, brace);
  // Trim a dangling separator left behind (": ", " - ", "=", etc.).
  return m.replace(/[\s:=-]+$/, "").trim();
}

// Distinct message labels from a set of logs, for the Exclude-box dropdown.
// Deduped case-insensitively on collapsed whitespace, keeping the first-seen text.
export function distinctMessages(logs: Array<{ message?: string | null }>): string[] {
  const seen = new Map<string, string>();
  for (const l of logs) {
    const label = messageLabel(l.message);
    if (!label) continue;
    const key = label.replace(/\s+/g, " ").toLowerCase();
    if (!seen.has(key)) seen.set(key, label);
  }
  return Array.from(seen.values());
}

// Distinct tags across a set of logs, for the Tags-box dropdown. Deduped
// case-insensitively, keeping the first-seen spelling.
export function distinctTags(logs: Array<{ filteredTags?: string[] | null }>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const l of logs) {
    for (const t of l.filteredTags || []) {
      const key = (t || "").trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
  }
  return out;
}

// Adaptive histogram bucket size (minutes) based on the selected window.
export function chooseBucketSize(
  fromDate?: string,
  fromTime?: string,
  toDate?: string,
  toTime?: string
): number {
  const start = dayjs(`${fromDate}T${fromTime}`);
  const end = dayjs(`${toDate}T${toTime}`);
  const diffMin = end.diff(start, "minute");
  if (diffMin <= 60) return 5; // <= 1 hour  -> 5-minute buckets
  if (diffMin <= 360) return 15; // <= 6 hours -> 15-minute buckets
  return 60; // > 6 hours -> 60-minute buckets
}

export interface TimeBucket {
  time: string;
  count: number;
}

// Bucket logs into {time, count} pairs snapped down to bucket boundaries.
export function computeTimeBuckets(
  logs: LogEntry[],
  bucketMinutes: number
): TimeBucket[] {
  if (!logs || logs.length === 0) return [];
  const buckets: Record<string, number> = {};
  for (const log of logs) {
    if (!log.timestamp) continue;
    const d = dayjs(log.timestamp);
    const key = d
      .startOf("minute")
      .subtract(d.minute() % bucketMinutes, "minute")
      .format("HH:mm");
    buckets[key] = (buckets[key] || 0) + 1;
  }
  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([time, count]) => ({ time, count }));
}
